"use client";

import { c as _c } from "react/compiler-runtime";
import { CompactModeProvider } from "@/contexts/CompactModeContext";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import type { ApiWave } from "@/generated/models/ApiWave";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { Transition } from "@headlessui/react";
import { markDropOpenReady } from "@/utils/monitoring/dropOpenTiming";
import { ArrowLeftIcon, ChatBubbleLeftRightIcon } from "@heroicons/react/24/outline";
import React, { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { SingleWaveDropChat } from "./SingleWaveDropChat";
interface SingleWaveDropWrapperProps {
  readonly drop: ApiDrop;
  readonly wave: ApiWave;
  readonly children: React.ReactNode;
  readonly onClose: () => void;
  readonly winningThreshold?: number | null | undefined;
  readonly winningThresholdMinDurationMs?: number | null | undefined;
  readonly isVotingClosed?: boolean | undefined;
  readonly isVotingControlsLocked?: boolean | undefined;
}
export const SingleWaveDropWrapper: React.FC<SingleWaveDropWrapperProps> = t0 => {
  const $ = _c(59);
  const {
    drop,
    wave,
    children,
    onClose,
    winningThreshold: t1,
    winningThresholdMinDurationMs: t2,
    isVotingClosed: t3,
    isVotingControlsLocked: t4
  } = t0;
  const winningThreshold = t1 === undefined ? null : t1;
  const winningThresholdMinDurationMs = t2 === undefined ? null : t2;
  const isVotingClosed = t3 === undefined ? false : t3;
  const isVotingControlsLocked = t4 === undefined ? false : t4;
  const [isChatOpen, setIsChatOpen] = useState(false);
  const isSmallScreen = useMediaQuery("(max-width: 1023px)");
  const readyKeyRef = useRef(null);
  let t5;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setIsChatOpen(_temp);
    };
    $[0] = t5;
  } else {
    t5 = $[0];
  }
  const toggleChat = t5;
  let t6;
  let t7;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      const browserWindow = globalThis.window;
      if (browserWindow === undefined) {
        return;
      }
      const handleClose = () => setIsChatOpen(false);
      browserWindow.addEventListener("single-drop:close-chat", handleClose);
      return () => browserWindow.removeEventListener("single-drop:close-chat", handleClose);
    };
    t7 = [];
    $[1] = t6;
    $[2] = t7;
  } else {
    t6 = $[1];
    t7 = $[2];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[3] !== isChatOpen || $[4] !== isSmallScreen) {
    t8 = () => {
      if (!isChatOpen || !isSmallScreen) {
        return;
      }
      const previousOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = previousOverflow;
      };
    };
    t9 = [isChatOpen, isSmallScreen];
    $[3] = isChatOpen;
    $[4] = isSmallScreen;
    $[5] = t8;
    $[6] = t9;
  } else {
    t8 = $[5];
    t9 = $[6];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[7] !== drop.id || $[8] !== wave.id) {
    t10 = () => {
      const readyKey = `${drop.id}:${wave.id}`;
      if (readyKeyRef.current === readyKey) {
        return;
      }
      readyKeyRef.current = readyKey;
      markDropOpenReady({
        dropId: drop.id,
        waveId: wave.id
      });
    };
    t11 = [drop.id, wave.id];
    $[7] = drop.id;
    $[8] = wave.id;
    $[9] = t10;
    $[10] = t11;
  } else {
    t10 = $[9];
    t11 = $[10];
  }
  useEffect(t10, t11);
  let t12;
  let t13;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <ArrowLeftIcon className="tw-h-5 tw-w-5 tw-flex-shrink-0 sm:tw-h-4 sm:tw-w-4" />;
    t13 = <span className="tw-hidden tw-text-sm tw-font-semibold sm:tw-inline">Close</span>;
    $[11] = t12;
    $[12] = t13;
  } else {
    t12 = $[11];
    t13 = $[12];
  }
  let t14;
  if ($[13] !== onClose) {
    t14 = <div className="tw-flex tw-items-center tw-gap-6"><button type="button" aria-label="Close panel" onClick={onClose} className="tw-flex tw-h-11 tw-w-11 tw-items-center tw-justify-center tw-gap-2 tw-rounded-full tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-950 tw-text-iron-300 tw-transition-all desktop-hover:hover:tw-text-white sm:tw-h-auto sm:tw-w-auto sm:tw-rounded-lg sm:tw-border-0 sm:tw-bg-transparent sm:tw-px-3 sm:tw-py-2">{t12}{t13}</button></div>;
    $[13] = onClose;
    $[14] = t14;
  } else {
    t14 = $[14];
  }
  const t15 = isChatOpen ? "Hide chat" : "Show chat";
  const t16 = `tw-flex tw-h-11 tw-w-11 tw-flex-shrink-0 tw-items-center tw-justify-center tw-gap-2 tw-rounded-full tw-border tw-border-solid tw-px-3 tw-text-sm tw-font-medium tw-backdrop-blur-md tw-transition-all sm:tw-h-auto sm:tw-w-auto sm:tw-rounded-lg sm:tw-py-2 ${isChatOpen ? "tw-border-iron-700 tw-bg-iron-800 tw-text-iron-100 desktop-hover:hover:tw-bg-iron-700" : "tw-border-iron-700 tw-bg-iron-950 tw-text-iron-300 desktop-hover:hover:tw-bg-iron-800 desktop-hover:hover:tw-text-iron-200"}`;
  let t17;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <ChatBubbleLeftRightIcon className="tw-h-5 tw-w-5 tw-flex-shrink-0 sm:tw-h-4 sm:tw-w-4" />;
    $[15] = t17;
  } else {
    t17 = $[15];
  }
  const t18 = isChatOpen ? "Hide" : "Show";
  let t19;
  if ($[16] !== t18) {
    t19 = <span className="tw-hidden sm:tw-inline">{t18} Chat</span>;
    $[16] = t18;
    $[17] = t19;
  } else {
    t19 = $[17];
  }
  let t20;
  if ($[18] !== t15 || $[19] !== t16 || $[20] !== t19) {
    t20 = <button onClick={toggleChat} aria-label={t15} className={t16}>{t17}{t19}</button>;
    $[18] = t15;
    $[19] = t16;
    $[20] = t19;
    $[21] = t20;
  } else {
    t20 = $[21];
  }
  let t21;
  if ($[22] !== t14 || $[23] !== t20) {
    t21 = <header className="tw-absolute tw-left-0 tw-right-3 tw-top-0 tw-z-20 tw-bg-iron-950/55 tw-bg-gradient-to-b tw-from-iron-950/75 tw-via-iron-950/55 tw-to-iron-950/10 tw-px-2 tw-pb-2 tw-pt-[calc(env(safe-area-inset-top,0px)+0.5rem)] tw-backdrop-blur-sm @[640px]:tw-px-8 lg:tw-px-8"><div className="tw-flex tw-w-full tw-items-center tw-justify-between">{t14}{t20}</div></header>;
    $[22] = t14;
    $[23] = t20;
    $[24] = t21;
  } else {
    t21 = $[24];
  }
  let t22;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <div className="tw-h-[calc(env(safe-area-inset-top,0px)+4rem)] tw-shrink-0" aria-hidden="true" />;
    $[25] = t22;
  } else {
    t22 = $[25];
  }
  let t23;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <div className="tw-h-[env(safe-area-inset-bottom,0px)] tw-shrink-0" aria-hidden="true" />;
    $[26] = t23;
  } else {
    t23 = $[26];
  }
  let t24;
  if ($[27] !== children) {
    t24 = <div className="tw-[scrollbar-gutter:stable] tw-h-full tw-overflow-y-auto tw-overscroll-contain tw-scrollbar-thin tw-scrollbar-track-iron-800 tw-scrollbar-thumb-iron-500 desktop-hover:hover:tw-scrollbar-thumb-iron-300">{t22}{children}{t23}</div>;
    $[27] = children;
    $[28] = t24;
  } else {
    t24 = $[28];
  }
  let t25;
  if ($[29] !== t21 || $[30] !== t24) {
    t25 = <div className="@container tw-relative tw-h-full tw-flex-1 tw-overflow-hidden">{t21}{t24}</div>;
    $[29] = t21;
    $[30] = t24;
    $[31] = t25;
  } else {
    t25 = $[31];
  }
  const t26 = `tw-hidden tw-h-full tw-flex-col tw-bg-black tw-transition-all tw-duration-300 tw-ease-in-out lg:tw-flex ${isChatOpen ? "tw-w-[400px] tw-border-y-0 tw-border-l tw-border-r-0 tw-border-solid tw-border-white/10" : "tw-w-0 tw-overflow-hidden tw-border-none"} `;
  let t27;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <div className="tw-pointer-events-none tw-absolute tw-left-0 tw-right-0 tw-top-0 tw-z-50 tw-h-12 tw-bg-gradient-to-b tw-from-black tw-to-transparent" />;
    $[32] = t27;
  } else {
    t27 = $[32];
  }
  let t28;
  if ($[33] !== drop || $[34] !== isVotingClosed || $[35] !== isVotingControlsLocked || $[36] !== wave || $[37] !== winningThreshold || $[38] !== winningThresholdMinDurationMs) {
    t28 = <div className="@container tw-relative tw-flex tw-flex-1 tw-flex-col tw-overflow-hidden">{t27}<SingleWaveDropChat key={drop.id} wave={wave} drop={drop} winningThreshold={winningThreshold} winningThresholdMinDurationMs={winningThresholdMinDurationMs} isVotingClosed={isVotingClosed} isVotingControlsLocked={isVotingControlsLocked} /></div>;
    $[33] = drop;
    $[34] = isVotingClosed;
    $[35] = isVotingControlsLocked;
    $[36] = wave;
    $[37] = winningThreshold;
    $[38] = winningThresholdMinDurationMs;
    $[39] = t28;
  } else {
    t28 = $[39];
  }
  let t29;
  if ($[40] !== t26 || $[41] !== t28) {
    t29 = <CompactModeProvider compact={true}><div className={t26}>{t28}</div></CompactModeProvider>;
    $[40] = t26;
    $[41] = t28;
    $[42] = t29;
  } else {
    t29 = $[42];
  }
  let t30;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <Transition.Child as={Fragment} enter="tw-duration-150 tw-ease-out" enterFrom="tw-opacity-0" enterTo="tw-opacity-100" leave="tw-duration-120 tw-ease-in" leaveFrom="tw-opacity-100" leaveTo="tw-opacity-0"><div className="tw-absolute tw-inset-0 tw-bg-black/60" onClick={toggleChat} /></Transition.Child>;
    $[43] = t30;
  } else {
    t30 = $[43];
  }
  let t31;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <div className="tw-relative tw-z-10 tw-flex tw-flex-none tw-items-center tw-px-2 tw-pb-2 tw-pt-[calc(env(safe-area-inset-top,0px)+0.75rem)] lg:tw-px-8"><button onClick={toggleChat} className="tw-flex tw-items-center tw-gap-2 tw-rounded-lg tw-border-0 tw-bg-transparent tw-px-3 tw-py-2 tw-text-white/70 tw-transition-colors desktop-hover:hover:tw-text-white"><ArrowLeftIcon className="tw-h-5 tw-w-5 tw-flex-shrink-0" /><span className="tw-text-sm tw-font-semibold">Close</span></button></div>;
    $[44] = t31;
  } else {
    t31 = $[44];
  }
  let t32;
  if ($[45] !== drop || $[46] !== isVotingClosed || $[47] !== isVotingControlsLocked || $[48] !== wave || $[49] !== winningThreshold || $[50] !== winningThresholdMinDurationMs) {
    t32 = <div className="tw-fixed tw-inset-y-0 tw-left-[var(--left-rail,0px)] tw-right-0 tw-z-[90] tw-h-[100dvh] tw-max-h-[100dvh] tw-overflow-hidden tw-overscroll-none lg:tw-hidden">{t30}<Transition.Child as={Fragment} enter="tw-duration-220 tw-transform tw-transition tw-ease-out" enterFrom="tw-translate-x-full" enterTo="tw-translate-x-0" leave="tw-duration-180 tw-transform tw-transition tw-ease-in" leaveFrom="tw-translate-x-0" leaveTo="tw-translate-x-full"><div className="@container tw-absolute tw-inset-0 tw-z-[100] tw-flex tw-h-full tw-min-h-0 tw-transform-gpu tw-flex-col tw-overflow-hidden tw-overscroll-none tw-bg-iron-950 tw-will-change-transform lg:tw-hidden">{t31}<div className="@container tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-hidden"><SingleWaveDropChat key={drop.id} wave={wave} drop={drop} winningThreshold={winningThreshold} winningThresholdMinDurationMs={winningThresholdMinDurationMs} isVotingClosed={isVotingClosed} isVotingControlsLocked={isVotingControlsLocked} /></div></div></Transition.Child></div>;
    $[45] = drop;
    $[46] = isVotingClosed;
    $[47] = isVotingControlsLocked;
    $[48] = wave;
    $[49] = winningThreshold;
    $[50] = winningThresholdMinDurationMs;
    $[51] = t32;
  } else {
    t32 = $[51];
  }
  let t33;
  if ($[52] !== isChatOpen || $[53] !== t32) {
    t33 = <CompactModeProvider compact={true}><Transition.Root show={isChatOpen} as={Fragment}>{t32}</Transition.Root></CompactModeProvider>;
    $[52] = isChatOpen;
    $[53] = t32;
    $[54] = t33;
  } else {
    t33 = $[54];
  }
  let t34;
  if ($[55] !== t25 || $[56] !== t29 || $[57] !== t33) {
    t34 = <div className="tw-flex tw-h-full tw-w-full tw-flex-col tw-overflow-hidden tw-overscroll-none tw-bg-iron-950 lg:tw-flex-row">{t25}{t29}{t33}</div>;
    $[55] = t25;
    $[56] = t29;
    $[57] = t33;
    $[58] = t34;
  } else {
    t34 = $[58];
  }
  return t34;
};
function _temp(prev) {
  return !prev;
}